import { useState, useCallback } from "react";
import useScanner from "../../hooks/useScanner";
import EditProductModal from "../EditProductModal/EditProductModal";
import LotSelectorModal from "../LotSelectorModal/LotSelectorModal";
import ProductSearchModal from "../ProductSearchModal/ProductSearchModal";
import { apiUpdateProduct } from "../../services/api";
import { generateTxId } from "../../utils/formatters";
import { parseGS1 } from "../../utils/gs1Parser";
import styles from "./Workflow.module.css";

export default function EditWorkflow({ products, user, onUpdateProduct, onTransaction, onBack }) {
  const [foundProduct, setFoundProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLotSelector, setShowLotSelector] = useState(false);
  const [lotMatches, setLotMatches] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [lastEdit, setLastEdit] = useState(null);
  const [error, setError] = useState("");

  const handleScan = useCallback((rawBarcode) => {
    setError("");
    setLastEdit(null);
    const gs1 = parseGS1(rawBarcode);
    const lookupBarcode = gs1.isGS1 ? gs1.gtin : rawBarcode;
    const matches = products.filter(p => (p.barcode || p.id) === lookupBarcode);
    if (matches.length === 1) {
      setFoundProduct(matches[0]);
      setShowEditModal(true);
    } else if (matches.length > 1) {
      setLotMatches(matches.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setShowLotSelector(true);
    } else {
      setError(`Item "${lookupBarcode}" not found in inventory.`);
    }
  }, [products]);

  const { error: scanError } = useScanner({
    onScan: handleScan,
    enabled: !showEditModal && !showLotSelector && !showSearch,
  });

  const handleSearchSelect = (lots) => {
    setShowSearch(false);
    setError("");
    setLastEdit(null);
    if (lots.length === 1) {
      setFoundProduct(lots[0]);
      setShowEditModal(true);
    } else if (lots.length > 1) {
      setLotMatches(lots.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setShowLotSelector(true);
    }
  };

  const handleLotSelect = (product) => {
    setShowLotSelector(false);
    setFoundProduct(product);
    setShowEditModal(true);
  };

  const handleEditSave = async (updates) => {
    try {
      const result = await apiUpdateProduct(user.token, foundProduct.id, updates);
      const updatedProduct = result.product;

      // Build change summary for audit trail
      const changes = [];
      const fieldsToCheck = ["type", "description", "manufacturer", "lotNumber", "expiryDate", "minOrderLine"];
      for (const field of fieldsToCheck) {
        const oldVal = foundProduct[field] ?? "";
        const newVal = updates[field] ?? "";
        if (String(oldVal) !== String(newVal)) {
          changes.push(`${field}: ${oldVal || "(empty)"} → ${newVal || "(empty)"}`);
        }
      }

      // Record edit transaction
      const tx = {
        id: generateTxId(),
        productId: updatedProduct.id,
        barcode: updatedProduct.barcode || foundProduct.barcode,
        lotNumber: updatedProduct.lotNumber || foundProduct.lotNumber,
        productName: updatedProduct.description || foundProduct.description,
        manufacturer: updatedProduct.manufacturer || foundProduct.manufacturer,
        type: "EDIT",
        quantity: 0,
        stockBefore: foundProduct.stock || 0,
        stockAfter: foundProduct.stock || 0,
        performedBy: user.name,
        performedByEmail: user.email,
        timestamp: new Date().toISOString(),
        notes: changes.join(", "),
      };

      onTransaction(tx, foundProduct.id, foundProduct.stock || 0);

      // Update local state
      onUpdateProduct(foundProduct.id, updatedProduct);

      setLastEdit({ productName: updatedProduct.description, changes: changes.length });
      setShowEditModal(false);
      setFoundProduct(null);
    } catch (err) {
      setError(`Failed to update product: ${err.message}`);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button onClick={onBack} className={styles.backBtn}>← Back</button>
        <div className={styles.workflowTitle}>Edit Product</div>
      </div>

      {lastEdit && (
        <div className={styles.success}>
          <div className={styles.successLabel}>Updated Successfully</div>
          <div className={styles.successName}>{lastEdit.productName}</div>
          <div className={styles.successMeta}>{lastEdit.changes} field{lastEdit.changes !== 1 ? "s" : ""} changed</div>
          <button onClick={() => setLastEdit(null)} className={styles.dismissBtn}>dismiss</button>
        </div>
      )}

      {(error || scanError) && (
        <div className={styles.error}>! {error || scanError}</div>
      )}

      <div className={styles.scannerCard}>
        <div className={styles.readyState}>
          <div className={styles.readyIcon}>✏</div>
          <div className={styles.readyTitle}>SCAN ITEM TO EDIT</div>
          <div className={styles.readyHint}>Point barcode scanner at item</div>
          <div className={styles.readyDivider}>or</div>
          <button onClick={() => setShowSearch(true)} className={styles.manualSearchBtn}>
            Search Manually
          </button>
        </div>
      </div>

      {showLotSelector && (
        <LotSelectorModal lots={lotMatches} mode="edit" onSelect={handleLotSelect}
          onClose={() => { setShowLotSelector(false); setLotMatches([]); }} />
      )}
      {showEditModal && foundProduct && (
        <EditProductModal product={foundProduct} onSave={handleEditSave}
          onClose={() => { setShowEditModal(false); setFoundProduct(null); }} />
      )}
      {showSearch && (
        <ProductSearchModal products={products} mode="edit" onSelect={handleSearchSelect}
          onClose={() => setShowSearch(false)} />
      )}
    </div>
  );
}
